/** @typedef {import('../models/registryTypes').RegistryEntry} RegistryEntry */
/** @typedef {import('../models/registryTypes').RoomMember} RoomMember */
/** @typedef {import('../models/registryTypes').CourtTeam} CourtTeam */

const ADDED_METHODS = new Set(["creator", "joined", "manual", "import"]);
const MEMBER_ROLES = new Set(["referee", "player", "spectator"]);
const PRESENCE = new Set(["online", "offline", "disconnected"]);

/**
 * @param {unknown} raw
 * @returns {RegistryEntry | null}
 */
export function normRegistryEntry(raw, roomId = "") {
  if (!raw || typeof raw !== "object") return null;
  const id = String(raw.id || "").trim();
  const playerId = String(raw.playerId || "").trim();
  const displayName = String(raw.displayName || raw.name || "").trim();
  if (!id || !playerId || !displayName) return null;
  const method = ADDED_METHODS.has(raw.addedMethod) ? raw.addedMethod : "manual";
  return {
    id,
    roomId: String(raw.roomId || roomId || "").trim(),
    playerId,
    displayName,
    avatar: raw.avatar ? String(raw.avatar) : null,
    userId: raw.userId ? String(raw.userId).trim() : null,
    addedAt: Number(raw.addedAt) || Date.now(),
    addedBy: String(raw.addedBy || "").trim(),
    addedMethod: method,
    active: raw.active !== false,
    ...(String(raw.gender || "").trim() ? { gender: String(raw.gender).trim() } : {}),
  };
}

/**
 * @param {unknown[]} list
 * @param {string} [roomId]
 * @returns {RegistryEntry[]}
 */
export function normRegistryEntries(list, roomId = "") {
  return (list || [])
    .map((row) => normRegistryEntry(row, roomId))
    .filter(Boolean);
}

/**
 * @param {unknown} raw
 * @returns {RoomMember | null}
 */
export function normRoomMember(raw, roomId = "") {
  if (!raw || typeof raw !== "object") return null;
  const id = String(raw.id || "").trim();
  const userId = String(raw.userId || "").trim();
  if (!id || !userId) return null;
  const role = MEMBER_ROLES.has(raw.role) ? raw.role : "player";
  const presence = PRESENCE.has(raw.presence) ? raw.presence : "offline";
  return {
    id,
    roomId: String(raw.roomId || roomId || "").trim(),
    userId,
    registryEntryId: raw.registryEntryId ? String(raw.registryEntryId).trim() : null,
    role,
    presence,
    joinedAt: Number(raw.joinedAt) || Date.now(),
  };
}

/**
 * @param {unknown[]} list
 * @param {string} [roomId]
 * @returns {RoomMember[]}
 */
export function normRoomMembers(list, roomId = "") {
  return (list || []).map((row) => normRoomMember(row, roomId)).filter(Boolean);
}

/**
 * @param {unknown} raw
 * @returns {CourtTeam}
 */
export function normCourtTeam(raw) {
  if (!raw || typeof raw !== "object") {
    return { name: "", type: "singles", slots: [] };
  }
  const name = String(raw.name || "").trim() || "Team";
  const type = raw.type === "doubles" ? "doubles" : "singles";
  const slots = (raw.slots || []).map((s) => normSlot(s)).filter(Boolean);
  return { name, type, slots };
}

/**
 * @param {unknown} raw
 */
function normSlot(raw) {
  if (!raw || typeof raw !== "object") return null;
  const registryEntryId = String(raw.registryEntryId || "").trim();
  const playerId = String(raw.playerId || "").trim();
  const displayName = String(raw.displayName || raw.name || "").trim();
  if (!registryEntryId || !playerId || !displayName) return null;
  return {
    registryEntryId,
    playerId,
    displayName,
    avatar: raw.avatar ? String(raw.avatar) : null,
    team: raw.team === "B" ? "B" : "A",
    position: Number.isFinite(raw.position) ? raw.position : 0,
  };
}

/**
 * @param {RegistryEntry} entry
 * @param {'A' | 'B'} team
 * @param {number} position
 */
export function entryToSnapshot(entry, team, position) {
  return {
    registryEntryId: entry.id,
    playerId: entry.playerId,
    displayName: entry.displayName,
    avatar: entry.avatar ?? null,
    team,
    position,
  };
}
